import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { usePortalImportHistory } from '@/hooks/use-portal'
import { getPortalImportDownloadUrl } from '@/services/portal.service'
import { Download, FileSpreadsheet, Loader2 } from 'lucide-react'
import { useState } from 'react'

export function PortalImportHistoryPage() {
  const { data: imports, isLoading } = usePortalImportHistory()
  const [downloadingId, setDownloadingId] = useState<number | null>(null)

  const handleDownload = async (importId: number) => {
    setDownloadingId(importId)
    try {
      const { download_url } = await getPortalImportDownloadUrl(importId)
      window.open(download_url, '_blank')
    } catch (error) {
      console.error('Download failed:', error)
    } finally {
      setDownloadingId(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="p-8 space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Import History</h1>
        <p className="text-muted-foreground">View past imports and download original files</p>
      </div>

      {!imports || imports.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileSpreadsheet className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No imports yet</p>
            <p className="text-sm text-muted-foreground">Import people to see history here</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>All Imports</CardTitle>
            <CardDescription>{imports.length} imports total</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="h-10 px-4 text-left text-sm font-medium">File</th>
                    <th className="h-10 px-4 text-left text-sm font-medium">Type</th>
                    <th className="h-10 px-4 text-left text-sm font-medium">Date</th>
                    <th className="h-10 px-4 text-left text-sm font-medium">Total</th>
                    <th className="h-10 px-4 text-left text-sm font-medium">Success</th>
                    <th className="h-10 px-4 text-left text-sm font-medium">Failed</th>
                    <th className="h-10 px-4 text-left text-sm font-medium">By</th>
                    <th className="h-10 px-4 text-left text-sm font-medium">Download</th>
                  </tr>
                </thead>
                <tbody>
                  {imports.map((imp) => (
                    <tr key={imp.id} className="border-b">
                      <td className="p-4 text-sm font-medium">{imp.original_filename}</td>
                      <td className="p-4">
                        <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 uppercase">
                          {imp.file_type}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {new Date(imp.created_at).toLocaleDateString()}
                      </td>
                      <td className="p-4 text-sm">{imp.total_rows}</td>
                      <td className="p-4 text-sm text-green-600">{imp.success_count}</td>
                      <td className="p-4 text-sm text-red-600">{imp.failure_count}</td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {imp.uploaded_by_name || '-'}
                      </td>
                      <td className="p-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(imp.id)}
                          disabled={downloadingId === imp.id}
                        >
                          {downloadingId === imp.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
